'use client';

import {
  deleteChatSession,
  renameChatSession,
  type ChatSession,
} from '@/app/services/chat';
import ModalDirective from '@/components/modal-directive';
import {
  fetchSidebarSearchHistory,
  patchSidebarSearchHistory,
  removeSidebarSearchHistory,
  sidebarHistoryKeys,
} from '@/components/sidebar-history/sidebar-history-cache';
import { SmartLink } from '@/components/smart-link';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckIcon,
  MoreVerticalIcon,
  PencilIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface SearchSidebarHistoryListProps {
  currentSessionId: string | null;
  isSearchRoute: boolean;
  isAuthenticated: boolean;
}

export function SearchSidebarHistoryList({
  currentSessionId,
  isSearchRoute,
  isAuthenticated,
}: SearchSidebarHistoryListProps) {
  const queryClient = useQueryClient();
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [savingTitleId, setSavingTitleId] = useState<string | null>(null);

  const { data: sessions, isLoading } = useQuery({
    queryKey: sidebarHistoryKeys.search,
    queryFn: fetchSidebarSearchHistory,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });

  const startEditing = (sessionId: string, title: string | null) => {
    setEditingSessionId(sessionId);
    setEditingTitle(title || '');
  };

  const cancelEditing = () => {
    setEditingSessionId(null);
    setEditingTitle('');
  };

  const saveTitle = async (sessionId: string, title: string | null) => {
    const nextTitle = editingTitle.trim();
    if (!nextTitle) {
      toast.error('标题不能为空');
      return;
    }
    if (nextTitle === (title || '').trim()) {
      cancelEditing();
      return;
    }

    setSavingTitleId(sessionId);
    try {
      await renameChatSession(sessionId, nextTitle);
      patchSidebarSearchHistory(queryClient, sessionId, {
        title: nextTitle,
        title_source: 'user',
      });
      cancelEditing();
      toast.success('标题已更新');
    } catch (error) {
      console.error(error);
      toast.error('更新标题失败，请稍后重试');
    } finally {
      setSavingTitleId(null);
    }
  };

  if (isLoading) {
    return Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="px-2 py-1">
        <Skeleton className="h-4 w-full rounded-md" />
      </div>
    ));
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="px-2 py-1.5 text-sm text-muted-foreground">
        暂无检索记录
      </div>
    );
  }

  return sessions.map((item: ChatSession) => {
    const active = isSearchRoute && currentSessionId === item.session_id;

    return (
      <div
        key={item.session_id}
        className={`group/item rounded-lg ${
          active
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'hover:bg-sidebar-accent'
        }`}
      >
        {editingSessionId === item.session_id ? (
          <div
            className="flex items-center gap-0.5 px-2 py-1"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <Input
              value={editingTitle}
              onChange={(event) => setEditingTitle(event.target.value)}
              className="h-7 border-0 bg-background/80 px-2 text-sm shadow-none"
              maxLength={500}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void saveTitle(item.session_id, item.title);
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelEditing();
                }
              }}
            />
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-7 w-7 shrink-0"
              disabled={savingTitleId === item.session_id}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void saveTitle(item.session_id, item.title);
              }}
            >
              <CheckIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              className="h-7 w-7 shrink-0"
              disabled={savingTitleId === item.session_id}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                cancelEditing();
              }}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="relative flex items-center">
            <SmartLink
              href={`/search?sid=${item.session_id}`}
              authenticated={isAuthenticated}
              mode="intent"
              className="min-w-0 flex-1 px-2 py-1.5 text-sm text-muted-foreground transition-colors group-hover/item:text-sidebar-accent-foreground"
            >
              <span className="line-clamp-1 break-all">
                {item.title || '未命名会话'}
              </span>
            </SmartLink>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className={`absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover/item:opacity-100 data-[state=open]:opacity-100 ${
                    active
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80'
                      : 'text-muted-foreground group-hover/item:bg-sidebar-accent group-hover/item:text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  <MoreVerticalIcon className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-36 rounded-2xl p-2 shadow-sm"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <DropdownMenuItem
                  className="rounded-xl"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    startEditing(item.session_id, item.title);
                  }}
                >
                  <PencilIcon className="h-4 w-4" />
                  重命名
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="rounded-xl text-destructive focus:text-destructive"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    ModalDirective.open({
                      title: '永久删除对话',
                      content: '删除后，该对话将不可恢复，确认删除吗？',
                      showCancel: true,
                      okText: '删除',
                      okVariant: 'destructive',
                      tone: 'danger',
                      onOk: async () => {
                        try {
                          await deleteChatSession(item.session_id);
                          removeSidebarSearchHistory(
                            queryClient,
                            item.session_id,
                          );
                          void queryClient.invalidateQueries({
                            queryKey: ['chat-history'],
                          });
                          toast.success('已删除会话');
                        } catch (error) {
                          console.error(error);
                          toast.error('删除失败，请稍后重试');
                        }
                      },
                    });
                  }}
                >
                  <Trash2Icon className="h-4 w-4" />
                  删除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    );
  });
}
